import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { view } from "@risingstack/react-easy-state";
import { Accordion, Breadcrumbs, Grid, InputStepper } from "@tine/designsystem-ui-react";
import find from "lodash-es/find";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import isEmpty from "lodash/isEmpty";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "wouter";

import { productImageUrl } from "../../common/imageUtils";
import { queueImpression, sendAddToCart, sendProductDetails } from "../../common/tracking";
import { PageType, type Product } from "../../common/types/productTypes";
import { LIST_TYPE } from "../../common/types/trackingTypes";
import useMediaQuery from "../../common/useMediaQuery";
import { mediumBreakpoint, stripPossibleHtmlExtension, titleCase, translateNutritionBaseUnit } from "../../common/utils";
import { isDataAvailable } from "../../common/utils/asyncDataUtils";
import PriceBomb from "../../components/common/PriceBomb";
import ProductInfo from "../../components/common/product/ProductInfo";
import ProductTile from "../../components/common/product/ProductTile";

import authStore from "../../stores/auth/authStore";
import loginState from "../../stores/auth/loginState";
import cartStore from "../../stores/cart/cartStore";
import categoryStore from "../../stores/categories/categoryStore";
import productStore from "../../stores/product/productStore";
import promotionStore from "../../stores/promotionStore";
import searchStore from "../../stores/searchStore";
import uiStore from "../../stores/uiStore";

import type { BreadcrumbsProps } from "@tine/designsystem-ui-react/src/components/breadcrumbs/Breadcrumbs";
import { Interweave } from "interweave";
import { trimEnd } from "lodash-es";
import Badges from "../../components/Badges";
import Button from "../../components/Button";
import Favorite from "../../components/Favorite";
import Labels from "../../components/Labels";
import LeadTimeWarning from "../../components/LeadTimeWarning";
import Loading from "../../components/Loading";
import NotFound from "../../components/NotFound";
import RelatedProducts from "../../components/RelatedProducts";
import WhiteSheet from "../../components/WhiteSheet";
import theme from "../../themes/theme";
import DisplayPricePdp from "./DisplayPricePdp";
import Nutrition from "./Nutrition";
import ProductDetailsPrint from "./ProductDetailsPrint";
import ProductWarnings from "./ProductWarnings";
import StockDisplay from "./StockDisplay";
import VariableWeightWarning from "./VariableWeightWarning";

const listName = "Produktdetaljside";

type ProductDetailsPageProps = {
   slug: string;
};

const productSearchDomain = theme.searchConfig.searchDomains.find((searchDomain) => searchDomain.type === "product");

const algoliaIndex = productSearchDomain?.indexName;

const AllergensDisplay = ({ product }: { product: Product | undefined }) => {
   if (isNil(product?.allergens) || isEmpty(product.allergens)) {
      return null;
   }

   const parts = product.allergens.split(". ");
   return parts.map((part) => <p key={part}>{trimEnd(part, ".")}.</p>);
};

const ProductDetailsPage = ({ slug }: ProductDetailsPageProps) => {
   const imageRef = useRef<HTMLImageElement>(null);
   const [imgLoading, setImageLoading] = useState(true);
   const [amount, setAmount] = useState(1);

   const isMobile = useMediaQuery(mediumBreakpoint);

   const urlKey = stripPossibleHtmlExtension(slug);
   const product = productStore.findProductByUrlKey(urlKey);
   const listInformation = {
      type: LIST_TYPE.CATEGORY,
      name: listName,
      length: 1
   };

   useEffect(() => {
      setImageLoading(true);
      if (!isNil(product)) {
         queueImpression(product, listName, 1);
         if (algoliaIndex) {
            searchStore.reportDetailsViewed(product.sku, algoliaIndex);
         }
      }
      setAmount(1);
   }, [slug, product]);

   useEffect(() => {
      if (!isUndefined(product)) {
         if (loginState.is("NOT_LOGGED_IN")) {
            sendProductDetails(product);
         } else if (
            !isUndefined(productStore.stockLevels[product.sku]) &&
            isDataAvailable(productStore.stockLevels[product.sku])
         ) {
            const stock_status_number = productStore.stockLevels[product.sku]?.data ?? undefined;
            let stock_status: string | undefined;

            if (isUndefined(stock_status_number)) {
               stock_status = undefined;
            } else if (stock_status_number >= 10) {
               stock_status = "10 eller fler på lager";
            } else if (stock_status_number > 0) {
               stock_status = "Mindre enn 10 på lager";
            } else {
               stock_status = "Ikke på lager";
            }
            sendProductDetails(product, stock_status);
         }
      }
   }, [loginState.currentState, product, product && productStore.stockLevels[product.sku]?.data]);

   const addToCart = async () => {
      if (!isNil(product)) {
         if (cartStore.addToCart(product.sku, amount)) {
            if (!isNil(imageRef)) {
               uiStore.addFlyingProduct(product.sku, imageRef);
            }
            sendAddToCart([{ sku: product.sku, qty: amount }], listInformation);
            if (algoliaIndex) {
               searchStore.reportAlgoliaAddToCart(product.sku, algoliaIndex);
            }
         } else {
            console.warn("Error occured during add to cart");
         }
      }
   };

   if (!productStore.productsLoaded) {
      return <Loading />;
   }

   if (isUndefined(product)) {
      return <NotFound />;
   }

   const inStock = 0 !== productStore.stockLevels[product.sku]?.data;
   const loggedIn = authStore.isLoggedIn();
   const replacementProduct = product.newSku ? productStore.resolveSku(product.newSku) : undefined;

   const catInfo = find(categoryStore.findCategoryKeysBySku(product.sku).map((key) => categoryStore.findCategoryByUrlKey(key)));

   const breadcrumbItems: BreadcrumbsProps["items"] =
      catInfo?.levels.map((c) => ({
         label: titleCase(c.name),
         href: c.fullUrl,
         as: Link
      })) || [];

   // Add product name to breadcrumb
   breadcrumbItems.length && breadcrumbItems.push({ label: product.name, href: "", as: Link });

   return (
      <>
         <div className="print:tw-hidden">
            <Helmet>
               <title>{product.name}</title>
               {!!product.description && <meta name="description" content={product.description.toString()} />}
            </Helmet>

            <div className="tw-mb-4">
               <Breadcrumbs items={breadcrumbItems} />
            </div>

            <WhiteSheet className="tw-mb-6 tw-py-12">
               <Grid cols={12} gap={4}>
                  <Grid.Col span={{ base: 12, lg: 4 }} className="tw-relative tw-mb-4 lg:tw-mb-0">
                     <div className="tw-relativ tw-h-full tw-w-full tw-items-center tw-justify-center">
                        <div className="tw-absolute tw-right-0 tw-w-12 tw-space-y-3 tw-text-center">
                           <Favorite sku={product.sku} listInformation={listInformation} position={1} />

                           <div className="tw-mr-2">
                              <Badges badges={product.badges} page={PageType.ProductDetailsPage} />
                           </div>
                        </div>

                        <div
                           className={`tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center ${imgLoading ? "" : "tw-hidden"}`}
                        >
                           <ArrowPathIcon className="tw-animate-spin" />
                        </div>

                        <div className={`tw-flex tw-w-full tw-items-center tw-justify-center ${imgLoading ? "tw-hidden" : ""}`}>
                           <img
                              ref={imageRef}
                              alt={`Produktbilde av ${product.name}`}
                              src={productImageUrl(product.sku, 380)}
                              onLoad={() => setImageLoading(false)}
                              className="tw-mx-auto tw-block tw-w-full tw-max-w-96"
                           />
                        </div>
                     </div>

                     <Labels labels={product.labels} page={PageType.ProductDetailsPage} sku={product.sku} />
                     {promotionStore.getPromotionsBySku(product.sku).map((promotion) => (
                        <PriceBomb size="large" key={promotion.banner}>
                           {promotion.banner}
                        </PriceBomb>
                     ))}
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, lg: 5 }} className="lg:mb-0 tw-mb-4 lg:tw-pr-4">
                     <h2 className="tw-mb-1">{product.name}</h2>
                     <div className="tw-mb-4">
                        Art.nr.: {product.sku} {product.epdNumber ? <>| EPD: {product.epdNumber}</> : null}
                     </div>
                     {theme.productAvailability.enabled && <StockDisplay product={product} />}

                     {loggedIn && <ProductWarnings product={product} />}

                     {!replacementProduct && (
                        <>
                           <VariableWeightWarning product={product} />
                           <LeadTimeWarning product={product} />
                           <DisplayPricePdp product={product} />
                           <div className="tw-flex tw-items-center tw-gap-2">
                              <div className="tw-flex md:tw-mr-3">
                                 <InputStepper key={product.sku} value={amount} min={1} onChange={setAmount} />
                              </div>
                              <div className="tw-mr-4">
                                 {loggedIn ? (
                                    <Button
                                       disabled={!inStock}
                                       variant="primary"
                                       onClick={addToCart}
                                       size="regular"
                                       data-testid="add-to-cart-product-details-button"
                                    >
                                       {inStock ? "Legg i handlekurven" : "Ikke på lager"}
                                    </Button>
                                 ) : (
                                    <Button variant="secondary" onClick={() => uiStore.showLoginModal()} size="regular">
                                       Logg inn for kjøp
                                    </Button>
                                 )}
                              </div>
                           </div>
                        </>
                     )}
                     {!!replacementProduct && <p>Dette produktet er ikke lengre tilgjengelig.</p>}
                  </Grid.Col>
                  <Grid.Col
                     span={{ base: 12, lg: 3 }}
                     className="tw-border-0 tw-px-5 lg:tw-border-l lg:tw-border-solid lg:tw-border-ink-border-subtle lg:tw-px-10"
                  >
                     <h3 className="tw-pb-4">{!replacementProduct ? "Andre anbefalte produkter" : "Erstattes med..."}</h3>
                     {!replacementProduct && <RelatedProducts skus={product.similarProducts} />}
                     {!!replacementProduct && (
                        <ProductTile
                           product={replacementProduct}
                           listInformation={{
                              type: LIST_TYPE.RELATED_PRODUCTS,
                              name: "Replacement product",
                              length: 1
                           }}
                           position={1}
                        />
                     )}
                  </Grid.Col>
               </Grid>
            </WhiteSheet>

            <WhiteSheet className="tw-py-12 md:tw-px-0">
               <Grid cols={12} gap={0}>
                  <Grid.Col span={{ base: 12, lg: 4 }} className="lg:tw-px-6">
                     {!isEmpty(product?.description) && (
                        <>
                           <h3>Beskrivelse</h3>
                           <p className="tw-mb-6">
                              <Interweave content={product.description?.toString() || ""} />
                           </p>
                        </>
                     )}

                     {!!product.ingredients &&
                        (isMobile ? (
                           <>
                              <Accordion label="Ingredienser" variant="compact" className="tw-border-t">
                                 <p className="tw-mb-6">
                                    <Interweave content={product.ingredients?.toString() || ""} />
                                 </p>
                              </Accordion>
                              {!isEmpty(product?.allergens) && (
                                 <Accordion label="Allergener" variant="compact" className="tw-border-t">
                                    <AllergensDisplay product={product} />
                                 </Accordion>
                              )}
                           </>
                        ) : (
                           <>
                              <h3>Ingredienser</h3>

                              <p className="tw-mb-6">
                                 <Interweave content={product.ingredients?.toString() || ""} />
                              </p>

                              {!isEmpty(product?.allergens) && (
                                 <>
                                    <h3>Allergener</h3>
                                    <AllergensDisplay product={product} />
                                 </>
                              )}
                           </>
                        ))}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, lg: 4 }} className="lg:tw-px-6">
                     {product.nutritions &&
                        product.nutritions.length > 0 &&
                        (isMobile ? (
                           <Accordion
                              label={`Næringsinnhold ${translateNutritionBaseUnit(product.nutritionBaseUnit)}`}
                              variant="compact"
                              className="tw-border-0"
                           >
                              <Nutrition data={product.nutritions} />
                           </Accordion>
                        ) : (
                           <>
                              <h3>{`Næringsinnhold ${translateNutritionBaseUnit(product.nutritionBaseUnit)}`}</h3>
                              <Nutrition data={product.nutritions} />
                           </>
                        ))}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, lg: 4 }} className="lg:tw-px-6">
                     {isMobile ? (
                        <Accordion label="Produktinformasjon" variant="compact" className="tw-border-t-0">
                           <ProductInfo product={product} />
                        </Accordion>
                     ) : (
                        <>
                           <h3>Produktinformasjon</h3>
                           <ProductInfo product={product} />
                        </>
                     )}
                  </Grid.Col>
               </Grid>
            </WhiteSheet>
            <div className="tw-pt-4">
               <Button size="compact" onClick={() => window.print()} variant="secondary" className="tw-float-end">
                  Skriv ut produktdatablad
               </Button>
            </div>
         </div>
         <ProductDetailsPrint product={product} />
      </>
   );
};

export default view(ProductDetailsPage);
